import { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import Cookies from 'js-cookie';
import BACKEND_URL from '../config';
import Input from './ui/Input';
import Button from './ui/Button';
import { IconTrash, IconPencil, IconCheck, IconX } from '@tabler/icons-react';

interface Characteristic {
    id: number;
    type: string;
    question_definition: string | null;
    answer_definition: string | null;
    answer_options: string[] | null;
    criterion_of_situation: string | null;
    criterion_of_action: string | null;
    description_value: string | null;
    remarks: string | null;
    comments: string | null;
}

interface Documentation {
    id: number;
    document: {
        id: string;
        title: string;
        content: string;
        url: string;
    };
    characteristic_data: Record<string, any>;
    summary: string;
    name: string;
}

interface Comment {
    id: number;
    comment: string;
    created_at: string;
    created_by: number;
}

interface CharacteristicsListProps {
    characteristics: Record<string, Characteristic>;
    documentation: Documentation[];
    loading: boolean;
}


const CommentSection = ({ comments, loading }: { comments: Comment[], loading: boolean }) => (
    <div className="mt-4 space-y-2">
        {loading ? (
            <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
        ) : (
            comments.map((comment) => (
                <div key={comment.id} className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700">{comment.comment}</p>
                    <p className="text-xs text-gray-500 mt-1">
                        {new Date(comment.created_at).toLocaleString()}
                    </p>
                </div>
            ))
        )}
    </div>
);

interface CharacteristicItemProps {
    characteristicKey: string;
    characteristic: Characteristic;
    associatedDocs: Documentation[];
    onCharacteristicUpdate: (characteristicKey: string, field: 'description_value' | 'criterion_of_situation', newValue: string) => void;
}

const CharacteristicItem = ({ characteristicKey, characteristic, associatedDocs, onCharacteristicUpdate }: CharacteristicItemProps) => {
    const [comments, setComments] = useState<Comment[]>([]);
    const [newComment, setNewComment] = useState('');
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [hasLoadedComments, setHasLoadedComments] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [localAssociatedDocs, setLocalAssociatedDocs] = useState<Documentation[]>(associatedDocs);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editedDescription, setEditedDescription] = useState(characteristic.description_value || '');
    const [isSavingDescription, setIsSavingDescription] = useState(false);
    const [isEditingCriterion, setIsEditingCriterion] = useState(false);
    const [editedCriterion, setEditedCriterion] = useState(characteristic.criterion_of_situation || '');
    const [isSavingCriterion, setIsSavingCriterion] = useState(false);

    const fetchComments = async () => {
        if (hasLoadedComments) return;
        
        setCommentsLoading(true);
        try {
            const response = await fetch(
                `${BACKEND_URL}/vnog/characteristic-comments/list/?characteristic_slug=${characteristicKey}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                        'X-CSRFToken': Cookies.get('csrftoken') || '',
                    },
                }
            );
            const data = await response.json();
            setComments(data);
            setHasLoadedComments(true);
        } catch (error) {
            console.error('Error fetching characteristic comments:', error);
            toast.error('Failed to fetch characteristic comments');
        } finally {
            setCommentsLoading(false);
        }
    };

    useEffect(() => {
        setLocalAssociatedDocs(associatedDocs);
    }, [associatedDocs]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    if (!hasLoadedComments) {
                        fetchComments();
                    }
                }
            },
            { threshold: 0.1 }
        );

        const element = document.getElementById(`characteristic-${characteristicKey}`);
        if (element) {
            observer.observe(element);
        }

        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, [characteristicKey, hasLoadedComments]);

    const handleCommentSubmit = async () => {
        if (!newComment.trim()) return;

        try {
            const response = await fetch(`${BACKEND_URL}/vnog/characteristic-comments/create/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    characteristic_slug: characteristicKey,
                    comment: newComment,
                }),
            });

            if (response.ok) {
                setNewComment('');
                setHasLoadedComments(false);
                fetchComments();
                toast.success('Comment added successfully');
            } else {
                throw new Error('Failed to add comment');
            }
        } catch (error) {
            console.error('Error adding characteristic comment:', error);
            toast.error('Failed to add comment');
        }
    };


    const handleRemoveFromCharacteristic = async (characteristicKey: string, answer: string, documentId: number) => {
        const response = await fetch(`${BACKEND_URL}/vnog/delete_characteristic_from_documentation/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'X-CSRFToken': Cookies.get('csrftoken') || '',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                documentation_id: documentId.toString(),
                characteristic_slug: characteristicKey,
                answer: answer
            }),
        });

        if (response.ok) {
            const data = await response.json();
            // Remove the document from local state
            setLocalAssociatedDocs(prevDocs => prevDocs.filter(doc => doc.id !== documentId));
            toast.success('Characteristic deleted successfully');
        } else {
            toast.error('Failed to delete characteristic');
        }
    };

    const handleSaveDescription = async () => {
        setIsSavingDescription(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/characteristics/edit-description/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    characteristic_id: characteristic.id,
                    description_value: editedDescription,
                }),
            });

            if (response.ok) {
                onCharacteristicUpdate(characteristicKey, 'description_value', editedDescription);
                setIsEditingDescription(false);
                toast.success('Description updated successfully');
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to update description');
            }
        } catch (error) {
            console.error('Error updating description:', error);
            toast.error('Failed to update description');
        } finally {
            setIsSavingDescription(false);
        }
    };

    const handleCancelDescriptionEdit = () => {
        setEditedDescription(characteristic.description_value || '');
        setIsEditingDescription(false);
    };

    const handleSaveCriterion = async () => {
        setIsSavingCriterion(true);
        try {
            const response = await fetch(`${BACKEND_URL}/vnog/characteristics/edit-description/`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'X-CSRFToken': Cookies.get('csrftoken') || '',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    characteristic_id: characteristic.id,
                    criterion_of_situation: editedCriterion,
                }),
            });

            if (response.ok) {
                onCharacteristicUpdate(characteristicKey, 'criterion_of_situation', editedCriterion);
                setIsEditingCriterion(false);
                toast.success('Criterion updated successfully');
            } else {
                const error = await response.json();
                toast.error(error.error || 'Failed to update criterion');
            }
        } catch (error) {
            console.error('Error updating criterion:', error);
            toast.error('Failed to update criterion');
        } finally {
            setIsSavingCriterion(false);
        }
    };

    const handleCancelCriterionEdit = () => {
        setEditedCriterion(characteristic.criterion_of_situation || '');
        setIsEditingCriterion(false);
    };

    return (
        <div id={`characteristic-${characteristicKey}`} className="border rounded-lg p-4">
            <div className="flex justify-between items-start">
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="font-medium text-lg">{characteristicKey}</h3>
                        <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">{characteristic.type}</span>
                    </div>
                    
                    {/* Description Value - Editable */}
                    <div className="mt-2">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-500">Description:</span>
                            {!isEditingDescription && (
                                <button
                                    onClick={() => setIsEditingDescription(true)}
                                    className="text-gray-400 hover:text-blue-500 transition-colors"
                                    title="Edit description"
                                >
                                    <IconPencil className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                        {isEditingDescription ? (
                            <div className="flex items-start gap-2">
                                <textarea
                                    value={editedDescription}
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                    className="flex-1 text-sm text-gray-600 border rounded-md p-2 min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter description..."
                                />
                                <div className="flex flex-col gap-1">
                                    <button
                                        onClick={handleSaveDescription}
                                        disabled={isSavingDescription}
                                        className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                                        title="Save"
                                    >
                                        <IconCheck className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={handleCancelDescriptionEdit}
                                        disabled={isSavingDescription}
                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                        title="Cancel"
                                    >
                                        <IconX className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-600">
                                {characteristic.description_value || <span className="italic text-gray-400">No description</span>}
                            </p>
                        )}
                    </div>

                    {/* Criterion of Situation - Editable */}
                    <div className="mt-2">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium text-gray-500">Criterion of Situation:</span>
                            {!isEditingCriterion && (
                                <button
                                    onClick={() => setIsEditingCriterion(true)}
                                    className="text-gray-400 hover:text-blue-500 transition-colors"
                                    title="Edit criterion"
                                >
                                    <IconPencil className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                        {isEditingCriterion ? (
                            <div className="flex items-start gap-2">
                                <textarea
                                    value={editedCriterion}
                                    onChange={(e) => setEditedCriterion(e.target.value)}
                                    className="flex-1 text-sm text-gray-600 border rounded-md p-2 min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Enter criterion of situation..."
                                />
                                <div className="flex flex-col gap-1">
                                    <button
                                        onClick={handleSaveCriterion}
                                        disabled={isSavingCriterion}
                                        className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                                        title="Save"
                                    >
                                        <IconCheck className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={handleCancelCriterionEdit}
                                        disabled={isSavingCriterion}
                                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                        title="Cancel"
                                    >
                                        <IconX className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-600">
                                {characteristic.criterion_of_situation || <span className="italic text-gray-400">No criterion defined</span>}
                            </p>
                        )}
                    </div>

                    {/* Answer Options */}
                    {characteristic.answer_options && characteristic.answer_options.length > 0 && (
                        <div className="mt-2">
                            <span className="text-xs font-medium text-gray-500">Answer Options:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                                {characteristic.answer_options.map((option, idx) => (
                                    <span key={idx} className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded">
                                        {option}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded ml-4">
                    {localAssociatedDocs.length} {localAssociatedDocs.length === 1 ? 'Document' : 'Documents'}
                </div>
            </div>

            {/* Characteristic Comments */}
            <div className="mt-4 border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Comments</h4>
                <CommentSection comments={comments} loading={commentsLoading} />
                <div className="flex flex-col gap-2">
                    <Input
                        label="Add a comment"
                        value={newComment}
                        onChange={setNewComment}
                        id={characteristicKey}
                        type="characteristic"
                        onSubmit={handleCommentSubmit}
                        placeholder="Add a comment about this characteristic..."
                    />
                    <Button className="w-fit" onClick={handleCommentSubmit}>Add Comment</Button>
                </div>
            </div>

            {/* Associated Documents */}
            {localAssociatedDocs.length > 0 && (
                <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Associated Documents:</h4>
                    <div className="space-y-2">
                        {localAssociatedDocs.map((doc, index) => (
                            <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                <span className="text-sm text-gray-600">{doc.name}</span>
                                <div>
                                    <button 
                                        className="text-red-500 hover:text-red-700 text-sm mr-8"
                                        onClick={() => handleRemoveFromCharacteristic(characteristicKey, doc.characteristic_data[characteristicKey], doc.id)}
                                    >
                                        <IconTrash className="w-4 h-4" />
                                    </button>
                                    <a 
                                        href={doc.document.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:text-blue-700 text-sm"
                                    >
                                        View Document
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const CharacteristicsList = ({ characteristics, documentation, loading }: CharacteristicsListProps) => {
    const [localCharacteristics, setLocalCharacteristics] = useState<Record<string, Characteristic>>(characteristics);

    useEffect(() => {
        setLocalCharacteristics(characteristics);
    }, [characteristics]);

    const handleCharacteristicUpdate = (characteristicKey: string, field: 'description_value' | 'criterion_of_situation', newValue: string) => {
        setLocalCharacteristics(prev => ({
            ...prev,
            [characteristicKey]: {
                ...prev[characteristicKey],
                [field]: newValue,
            }
        }));
    };

    const getCharacteristicStats = () => {
        const stats = {
            totalCharacteristics: Object.keys(localCharacteristics).length,
            totalDocuments: documentation.length,
            characteristicsWithDocuments: 0,
            documentsWithCharacteristics: 0,
        };

        // Count how many characteristics are used in documents
        const usedCharacteristics = new Set<string>();
        documentation.forEach(doc => {
            Object.keys(doc.characteristic_data).forEach(char => {
                usedCharacteristics.add(char);
            });
        });
        stats.characteristicsWithDocuments = usedCharacteristics.size;

        // Count how many documents have characteristics
        stats.documentsWithCharacteristics = documentation.filter(doc => 
            Object.keys(doc.characteristic_data).length > 0
        ).length;

        return stats;
    };

    const getDocumentsForCharacteristic = (characteristicKey: string) => {
        return documentation.filter(doc => 
            characteristicKey in doc.characteristic_data
        );
    };

    const stats = getCharacteristicStats();

    return (
        <div className="bg-white rounded-lg shadow-md p-4">
            <h2 className="text-xl font-semibold mb-4">Characteristics</h2>
            
            {/* Summary Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-blue-600">Total Characteristics</h3>
                    <p className="text-2xl font-bold text-blue-700">{stats.totalCharacteristics}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-green-600">Used Characteristics</h3>
                    <p className="text-2xl font-bold text-green-700">{stats.characteristicsWithDocuments}</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-purple-600">Total Documents</h3>
                    <p className="text-2xl font-bold text-purple-700">{stats.totalDocuments}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-orange-600">Documents with Characteristics</h3>
                    <p className="text-2xl font-bold text-orange-700">{stats.documentsWithCharacteristics}</p>
                </div>
            </div>

            {/* Characteristics List */}
            {loading ? (
                <div>Loading characteristics...</div>
            ) : (
                <div className="space-y-6">
                    {Object.entries(localCharacteristics).map(([key, value]) => (
                        <CharacteristicItem
                            key={key}
                            characteristicKey={key}
                            characteristic={value}
                            associatedDocs={getDocumentsForCharacteristic(key)}
                            onCharacteristicUpdate={handleCharacteristicUpdate}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default CharacteristicsList; 